import type { CSSProperties } from 'react';
import { normalizeUiDensity } from '../services/ports/uiDensityPreference';

export function getSystemDensityMetrics(density: number) {
  const ratio = normalizeUiDensity(density) / 100;
  return {
    spaceScale: 0.5 + 0.5 * ratio,
    sizeScale: 0.75 + 0.25 * ratio,
    fontScale: 0.8 + 0.2 * ratio,
  };
}

export function getSystemDensityVariables(density: number) {
  const { spaceScale, sizeScale, fontScale } = getSystemDensityMetrics(density);
  return {
    '--cpq-ui-space-scale': String(spaceScale),
    '--cpq-ui-size-scale': String(sizeScale),
    '--cpq-ui-font-scale': String(fontScale),
  };
}

export function getQuoteDensityMetrics(density: number) {
  const ratio = normalizeUiDensity(density) / 100;
  const between = (min: number, max: number) => min + (max - min) * ratio;
  return {
    columnScale: between(0.8, 1),
    // Rc virtual tables use one integer estimate for regular rows. Keep the
    // CSS height on the same integer boundary to prevent cumulative drift.
    rowHeight: Math.round(between(24, 40)),
    groupRowHeight: Math.round(between(24, 40)),
    headerHeight: between(24, 40),
    groupHeaderHeight: between(20, 32),
    summaryHeight: between(24, 44),
    fontSize: between(10, 13),
    lineHeight: between(14, 16),
    cellPaddingY: between(1, 4),
    cellPaddingX: between(3, 8),
    headerPaddingY: between(1, 4),
    summaryPadding: between(2, 8),
    imageSize: between(18, 32),
    groupImageSize: between(22, 36),
    controlHeight: between(20, 24),
    controlFontSize: between(10, 13),
    checkboxTarget: between(20, 24),
    expanderSize: between(16, 22),
    quantityActionWidth: between(14, 20),
    toolbarGap: between(4, 16),
    toolbarGapY: between(4, 10),
    toolbarPaddingY: between(4, 10),
    toolbarPaddingX: between(4, 12),
    toolbarControlHeight: between(24, 32),
  };
}

export function getQuoteDensityStyle(density: number): CSSProperties {
  const { columnScale: _columnScale, ...metrics } =
    getQuoteDensityMetrics(density);
  return Object.fromEntries(
    Object.entries(metrics).map(([name, value]) => [
      `--cpq-ui-${name.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}`,
      `${value}px`,
    ]),
  ) as CSSProperties;
}

export function quoteDensityColumnWidth(baseWidth: number, density: number) {
  return Math.round(baseWidth * getQuoteDensityMetrics(density).columnScale);
}

export function quoteDensityBaseColumnWidth(width: number, density: number) {
  return width / getQuoteDensityMetrics(density).columnScale;
}
